// Blueprint tile drawing with ratio-to-centre effect
// Draws tiles using wedge-based regions where minority types get smaller wedges

import { TILE_COLORS } from '../../gdd-types/tileTypes.mjs';
import { analyseTwoTypeRun } from '../../gdd-types/tileModel.mjs';
import { hexCorners, getEffectiveSide } from '../../math/hexMath.mjs';

/**
 * Draw a blueprint tile with ratio-to-centre visual effect.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} tile - Tile object
 * @param {number} cx - screen center x
 * @param {number} cy - screen center y
 * @param {number} size - hex size in screen pixels
 * @param {number} alpha - opacity (0..1)
 * @param {boolean} highlight - draw highlight border
 * @param {boolean} showInvalid - highlight invalid tiles for debugging
 */
export function drawBlueprintTile(ctx, tile, cx, cy, size, alpha = 1, highlight = false, showInvalid = false) {
  ctx.save();
  ctx.globalAlpha = alpha;

  const corners = hexCorners(cx, cy, size);
  const run = analyseTwoTypeRun(tile.sides);

  if (run.invalid && showInvalid) {
    // Fallback: draw wedges like the old renderer but with a red outline
    drawSimpleWedges(ctx, tile, cx, cy, size, corners);
    drawHexOutline(ctx, corners, 'rgba(255,0,0,0.8)', 2);
  } else if (run.b === null) {
    // Mono-type: fill whole hex
    drawHexFill(ctx, corners, TILE_COLORS[run.a]);
  } else {
    // Two-type with ratio-to-centre effect
    drawTwoTypeRatioHex(ctx, tile, cx, cy, size, corners, run);
  }

  if (highlight) {
    drawHexOutline(ctx, corners, '#ffd700', 2.5);
  }

  ctx.restore();
}

/**
 * Draw the two-type ratio-to-centre hex.
 * 1) Fill entire hex with majority color (no gaps possible).
 * 2) Overdraw minority wedges as trapezoids that only reach partway to center.
 */
function drawTwoTypeRatioHex(ctx, tile, cx, cy, size, corners, run) {
  const { a, b, aCount } = run;

  // Fill entire hex with majority (type A) color — seamless base
  drawHexFill(ctx, corners, TILE_COLORS[a]);

  // How far minority wedges reach toward center (0 = edge only, 1 = full center).
  // Fewer minority sides → reaches less toward center.
  const bCount = 6 - aCount;
  const bReach = lerp(0.15, 0.85, bCount / 6);

  // Overdraw each minority (type B) wedge as a trapezoid
  for (let i = 0; i < 6; i++) {
    const effectiveType = getEffectiveSide(tile.sides, tile.rotation, i);
    if (effectiveType === a) continue; // majority already drawn

    const c1 = corners[i];
    const c2 = corners[(i + 1) % 6];

    // Inner points: lerp from corner toward center by bReach
    const inner1x = lerp(c1.x, cx, bReach);
    const inner1y = lerp(c1.y, cy, bReach);
    const inner2x = lerp(c2.x, cx, bReach);
    const inner2y = lerp(c2.y, cy, bReach);

    ctx.beginPath();
    ctx.moveTo(inner1x, inner1y);
    ctx.lineTo(c1.x, c1.y);
    ctx.lineTo(c2.x, c2.y);
    ctx.lineTo(inner2x, inner2y);
    ctx.closePath();
    ctx.fillStyle = TILE_COLORS[effectiveType];
    ctx.fill();
  }

  // Subtle boundary lines where type changes
  drawTypeBoundary(ctx, tile, cx, cy, size, corners, run);
}

/**
 * Draw a subtle line at the boundary between the two type regions.
 */
function drawTypeBoundary(ctx, tile, cx, cy, size, corners, run) {
  const { a, aCount } = run;
  const bCount = 6 - aCount;
  const bReach = lerp(0.15, 0.85, bCount / 6);

  for (let i = 0; i < 6; i++) {
    const t1 = getEffectiveSide(tile.sides, tile.rotation, i);
    const t2 = getEffectiveSide(tile.sides, tile.rotation, (i + 1) % 6);
    if (t1 !== t2) {
      const corner = corners[(i + 1) % 6];
      const innerX = lerp(corner.x, cx, bReach);
      const innerY = lerp(corner.y, cy, bReach);
      ctx.beginPath();
      ctx.moveTo(innerX, innerY);
      ctx.lineTo(corner.x, corner.y);
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
}

function drawSimpleWedges(ctx, tile, cx, cy, size, corners) {
  for (let i = 0; i < 6; i++) {
    const sideType = getEffectiveSide(tile.sides, tile.rotation, i);
    const c1 = corners[i];
    const c2 = corners[(i + 1) % 6];
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(c1.x, c1.y);
    ctx.lineTo(c2.x, c2.y);
    ctx.closePath();
    ctx.fillStyle = TILE_COLORS[sideType];
    ctx.fill();
  }
}

function drawHexFill(ctx, corners, color) {
  ctx.beginPath();
  for (let i = 0; i < corners.length; i++) {
    if (i === 0) ctx.moveTo(corners[i].x, corners[i].y);
    else ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

function drawHexOutline(ctx, corners, color, lineWidth) {
  ctx.beginPath();
  for (let i = 0; i < corners.length; i++) {
    if (i === 0) ctx.moveTo(corners[i].x, corners[i].y);
    else ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

/**
 * Draw a valid-placement indicator (dashed hex outline).
 */
export function drawPlacementIndicator(ctx, cx, cy, size) {
  const corners = hexCorners(cx, cy, size);
  ctx.beginPath();
  for (let i = 0; i < corners.length; i++) {
    if (i === 0) ctx.moveTo(corners[i].x, corners[i].y);
    else ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.stroke();
  ctx.setLineDash([]);
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}
